<template>
  <div class="content">
    <div class="header d-flex justify-content-between align-items-center p-2">
      <div class="title">
        <h3 class="h3 mb-n2 bold">FIC - Prestação de Contas</h3>
        <span class="subtitle-2 grey--text text--darken-1">
          Formulário de Informações Complementares para Lei de informática
        </span>
      </div>
      <div class="actions d-flex flex-row align-center">
        <v-btn class="elevation-4 mr-2" @click="doLoad">
          <v-icon left dark>mdi-refresh</v-icon>
          <span style="font-size: 12px">Atualizar</span>
        </v-btn>
        <v-btn
          v-if="isLI"
          class="elevation-4 mr-2"
          :loading="calculando"
          :disabled="items.length === 0"
          @click="doCalcularListagem"
        >
          <v-icon left dark>mdi-calculator</v-icon>
          <span style="font-size: 12px">Calcular P,D&I</span>
        </v-btn>
        <v-btn
          v-if="isLI && items.length > 0"
          class="elevation-4 mr-2"
          :loading="travando"
          :color="itemAtual.travado ? 'green' : 'red'"
          dark
          @click="doTravarListagem"
        >
          <v-icon left>{{ itemAtual.travado ? 'mdi-lock-open' : 'mdi-lock' }}</v-icon>
          <span style="font-size: 12px">{{ itemAtual.travado ? 'Destravar' : 'Travar' }}</span>
        </v-btn>
      </div>
    </div>

    <div class="d-flex align-center mt-2 mb-2">
      <year-select
        style="max-width: 150px"
        class="mr-3"
      />
      <v-spacer />
      <v-btn
        class="elevation-4"
        style="background-color: #1c1c44; color: white"
        :disabled="items.length > 0"
        @click="openAdd"
      >
        <v-icon left>mdi-plus-box-outline</v-icon>
        Adicionar
      </v-btn>
    </div>

    <div class="body">
      <v-progress-linear v-if="loading" indeterminate color="#1c1c44" />

      <template v-if="!loading && items.length === 0">
        <div class="text-center grey--text pa-6">Nenhum registro encontrado</div>
      </template>

      <template v-for="(item, idx) in items">
        <v-card :key="'fic-' + idx" class="mb-4 elevation-2" outlined>
          <v-card-title class="py-2 px-4 d-flex align-center" style="background-color: #f5f5f5">
            <span class="subtitle-1 font-weight-bold" style="color: #1c1c44">
              Formulário de Informações Complementares - {{ item.ano_base }}
            </span>
            <v-chip
              v-if="item.travado"
              x-small
              color="red"
              text-color="white"
              class="ml-2"
            >
              <v-icon x-small left>mdi-lock</v-icon>
              Travada
            </v-chip>
            <v-spacer />
            <v-btn small text class="mr-1" color="#1c1c44" @click="openEdit(item)">
              <v-icon small left>mdi-pencil</v-icon>
              Editar
            </v-btn>
            <v-btn small text color="red" @click.stop="confirmDelete(item)" :disabled="item.travado">
              <v-icon small left>mdi-delete</v-icon>
              Excluir
            </v-btn>
          </v-card-title>
          <v-divider />

          <v-card-text class="pa-4">
            <template v-if="isLI">
              <!-- Cards de Quantidades Calculadas -->
              <div class="subtitle-2 font-weight-bold mb-2" style="color: #1c1c44">
                Dados gerais
              </div>
              <v-row dense>
                <v-col
                  v-for="(card, k) in quantidadesCards"
                  :key="'card-' + k"
                  cols="6"
                  sm="3"
                >
                  <v-card outlined class="pa-3 text-center" style="border-radius: 8px">
                    <v-icon :color="card.color" class="mb-1">{{ card.icon }}</v-icon>
                    <div class="text-h5 font-weight-bold" :style="{ color: card.color }">
                      {{ item[card.key] != null ? item[card.key] : '–' }}
                    </div>
                    <div class="caption grey--text text--darken-1">{{ card.name }}</div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Tabela Faturamento -->
              <div class="subtitle-2 font-weight-bold mt-4 mb-2" style="color: #1c1c44">
                Faturamento Bruto (R$)
              </div>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Descrição</th>
                      <th class="text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="col in faturamentoFields" :key="col.key">
                      <td>{{ col.name }}</td>
                      <td class="text-right">{{ formatMoney(item[col.key]) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Tabela Exportações -->
              <div class="subtitle-2 font-weight-bold mt-4 mb-2" style="color: #1c1c44">
                Exportações (R$)
              </div>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Descrição</th>
                      <th class="text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="col in exportacoesFields" :key="col.key">
                      <td>{{ col.name }}</td>
                      <td class="text-right">{{ formatMoney(item[col.key]) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- Tabela Importações -->
              <div class="subtitle-2 font-weight-bold mt-4 mb-2" style="color: #1c1c44">
                Importações (R$)
              </div>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Descrição</th>
                      <th class="text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="col in importacoesFields" :key="col.key">
                      <td>{{ col.name }}</td>
                      <td class="text-right">{{ formatMoney(item[col.key]) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </template>

            <template v-else>
              <!-- LB: Representante -->
              <div class="subtitle-2 font-weight-bold mb-2" style="color: #1c1c44">
                Representante
              </div>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Campo</th>
                      <th class="text-left">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="col in representanteFields" :key="col.key">
                      <td>{{ col.name }}</td>
                      <td>{{ item[col.key] || '–' }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- LB: Dados da Empresa -->
              <div class="subtitle-2 font-weight-bold mt-4 mb-2" style="color: #1c1c44">
                Dados da Empresa
              </div>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Campo</th>
                      <th class="text-left">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="col in empresaFields" :key="col.key">
                      <td>{{ col.name }}</td>
                      <td>{{ formatFieldValue(item, col) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- LB: Dispêndios -->
              <div class="subtitle-2 font-weight-bold mt-4 mb-2" style="color: #1c1c44">
                Dispêndios
              </div>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Campo</th>
                      <th class="text-left">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="col in dispendiosFields" :key="col.key">
                      <td>{{ col.name }}</td>
                      <td>{{ item[col.key] || '–' }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>

              <!-- LB: Programa de Formação -->
              <div class="subtitle-2 font-weight-bold mt-4 mb-2" style="color: #1c1c44">
                Programa de Formação
              </div>
              <v-simple-table dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Campo</th>
                      <th class="text-left">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="col in formacaoFields" :key="col.key">
                      <td>{{ col.name }}</td>
                      <td>{{ formatFieldValue(item, col) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </template>
          </v-card-text>
        </v-card>
      </template>
    </div>

    <FICForm
      :opened.sync="isFormVisible"
      :value.sync="formData"
      :beneficio="beneficioSelecionado"
      :ano-base="anoBase"
      :error-message="errorMessage"
      :travando="travando"
      @save="doSave"
      @travar="doTravar"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import FICService from "@/services/fic.service";
import { BeneficiosEnum } from "@/core/enums/beneficios";
import { getFieldsLI, getFieldsLB } from "@/templates/dispendio/fic.fields";

export default {
  components: {
    FICForm: () => import("@/templates/dispendio/fic.form.vue"),
    YearSelect: () => import("@/components/year-select.vue"),
  },
  data() {
    return {
      ficService: new FICService(),
      items: [],
      loading: false,
      isFormVisible: false,
      formData: {},
      errorMessage: "",
      calculando: false,
      travando: false,
      beneficioSelecionado: BeneficiosEnum.LEI_DE_INFORMATICA,
    };
  },
  computed: {
    ...mapGetters(["clientId", "anoBase"]),
    isLI() {
      return this.beneficioSelecionado === BeneficiosEnum.LEI_DE_INFORMATICA;
    },
    fieldsLI() {
      return getFieldsLI(this.$fieldTypes);
    },
    fieldsLB() {
      return getFieldsLB(this.$fieldTypes);
    },
    quantidadesCards() {
      return this.fieldsLI.quantidadesCalculadas;
    },
    faturamentoFields() {
      return this.fieldsLI.faturamento;
    },
    exportacoesFields() {
      return this.fieldsLI.exportacoes;
    },
    importacoesFields() {
      return this.fieldsLI.importacoes;
    },
    representanteFields() {
      return this.fieldsLB.representante;
    },
    empresaFields() {
      return this.fieldsLB.empresa;
    },
    dispendiosFields() {
      return this.fieldsLB.dispendios;
    },
    formacaoFields() {
      return this.fieldsLB.formacao;
    },
    itemAtual() {
      return this.items.length > 0 ? this.items[0] : {};
    },
  },
  watch: {
    anoBase() {
      this.formData = {};
      this.doLoad();
    },
    clientId() {
      this.formData = {};
      this.doLoad();
    },
  },
  created() {
    this.doLoad();
  },
  methods: {
    formatMoney(value) {
      if (value == null) return "–";
      return Number(value).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
      });
    },
    formatFieldValue(item, col) {
      const val = item[col.key];
      if (val == null) return "–";
      if (col.type === this.$fieldTypes.MONEY) return this.formatMoney(val);
      if (col.type === this.$fieldTypes.SWITCH) return val ? "Sim" : "Não";
      return val;
    },
    async doLoad() {
      if (!this.clientId || !this.anoBase) return;

      this.loading = true;
      try {
        const response = this.isLI
          ? await this.ficService.getListLI(this.clientId, this.anoBase)
          : await this.ficService.getListLB(this.clientId, this.anoBase);

        this.items = response.data || [];
      } catch (error) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro",
          text: "Erro ao carregar registros FIC.",
        });
      } finally {
        this.loading = false;
      }
    },
    async openAdd() {
      this.errorMessage = "";
      this.formData = {};
      const [calcResponse, qtdePatentes, qtdePublicacoes] = await Promise.all([
        this.isLI ? this.ficService.calcularQuantidades(this.clientId, this.anoBase).catch(() => null) : Promise.resolve(null),
        this.fetchPatentesRequeridas(),
        this.fetchPublicacoesProjetos(),
      ]);

      const dados = { ano_base: this.anoBase, qtde_patentes: qtdePatentes, qtde_publicacoes: qtdePublicacoes };

      if (calcResponse?.data) {
        const c = calcResponse.data;
        dados.qtde_pessoal_total        = c.qtde_pessoal_total        ?? null;
        dados.qtde_pessoal_pdi          = c.qtde_pessoal_pdi          ?? null;
        dados.qtde_pessoal_superior     = c.qtde_pessoal_superior     ?? null;
        dados.qtde_pessoal_superior_pdi = c.qtde_pessoal_superior_pdi ?? null;
        dados.total_prod_incentivados   = c.total_prod_incentivados   ?? null;
        dados.total_tecnac              = c.total_tecnac              ?? null;
        dados.total_exportacao_incentivada = c.total_exportacao_incentivada ?? null;
      }

      this.formData = dados;
      this.isFormVisible = true;
    },
    async openEdit(item) {
      this.errorMessage = "";
      const [qtdePatentes, qtdePublicacoes] = await Promise.all([
        this.fetchPatentesRequeridas(),
        this.fetchPublicacoesProjetos(),
      ]);
      this.formData = { ...item, qtde_patentes: qtdePatentes, qtde_publicacoes: qtdePublicacoes };
      this.isFormVisible = true;
    },
    async doSave(data) {
      this.errorMessage = "";
      try {
        if (data.id) {
          this.isLI
            ? await this.ficService.updateLI(this.clientId, data.id, data)
            : await this.ficService.updateLB(this.clientId, data.id, data);
        } else {
          const payload = { ...data, ano_base: this.anoBase };
          this.isLI
            ? await this.ficService.saveLI(this.clientId, payload)
            : await this.ficService.saveLB(this.clientId, payload);
        }

        this.$notify({
          group: "geral",
          duration: 5000,
          type: "success",
          title: "Sucesso",
          text: `FIC ${data.id ? "atualizada" : "cadastrada"} com sucesso!`,
        });

        this.isFormVisible = false;
        this.formData = {};
        this.doLoad();
      } catch (error) {
        const msg =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Erro ao salvar FIC.";
        this.errorMessage = msg;
      }
    },
    async doTravar() {
      if (!this.formData.id) return;

      this.travando = true;
      try {
        const response = await this.ficService.travarLI(this.clientId, this.formData.id);

        this.formData = {
          ...this.formData,
          travado: response.data.travado,
        };

        this.$notify({
          group: "geral",
          duration: 3000,
          type: "success",
          title: "Sucesso",
          text: response.data.message,
        });

        this.doLoad();
      } catch (error) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro",
          text: "Erro ao travar/destravar FIC.",
        });
      } finally {
        this.travando = false;
      }
    },
    async doCalcularListagem() {
      if (!this.clientId || !this.anoBase) return;

      const item = this.itemAtual;
      if (!item.id) return;

      this.calculando = true;
      try {
        const [calcResponse, patentesResponse, publicacoesResponse] = await Promise.all([
          this.ficService.calcularQuantidades(this.clientId, this.anoBase),
          this.fetchPatentesRequeridas(),
          this.fetchPublicacoesProjetos(),
        ]);

        const c = calcResponse.data;
        const qtde_patentes    = patentesResponse;
        const qtde_publicacoes = publicacoesResponse;

        const updatedData = {
          ...item,
          qtde_pessoal_total:        c.qtde_pessoal_total,
          qtde_pessoal_pdi:          c.qtde_pessoal_pdi,
          qtde_pessoal_superior:     c.qtde_pessoal_superior,
          qtde_pessoal_superior_pdi: c.qtde_pessoal_superior_pdi,
          qtde_patentes,
          qtde_publicacoes,
          total_prod_incentivados:      c.total_prod_incentivados,
          total_tecnac:                 c.total_tecnac,
          total_exportacao_incentivada: c.total_exportacao_incentivada,
        };

        await this.ficService.updateLI(this.clientId, item.id, updatedData);

        this.$notify({
          group: "geral",
          duration: 3000,
          type: "success",
          title: "Sucesso",
          text: `Quantidades calculadas e salvas com sucesso!`,
        });

        this.doLoad();
      } catch (error) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro",
          text: "Erro ao calcular quantidades de P,D&I.",
        });
      } finally {
        this.calculando = false;
      }
    },
    async doTravarListagem() {
      const item = this.itemAtual;
      if (!item.id) return;

      this.travando = true;
      try {
        const response = await this.ficService.travarLI(this.clientId, item.id);

        this.$notify({
          group: "geral",
          duration: 3000,
          type: "success",
          title: "Sucesso",
          text: response.data.message,
        });

        this.doLoad();
      } catch (error) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Erro",
          text: "Erro ao travar/destravar FIC.",
        });
      } finally {
        this.travando = false;
      }
    },
    async fetchPatentesRequeridas() {
      if (!this.clientId || !this.anoBase) return 0;
      try {
        const response = await this.ficService.getPatentesRequeridas(this.clientId, this.anoBase);
        return response.data?.qtde_patentes ?? 0;
      } catch {
        return 0;
      }
    },
    async fetchPublicacoesProjetos() {
      if (!this.clientId || !this.anoBase) return 0;
      try {
        const response = await this.ficService.getPublicacoesProjetos(this.clientId, this.anoBase);
        return response.data?.qtde_publicacoes ?? 0;
      } catch {
        return 0;
      }
    },
    confirmDelete(item) {
      if (item.travado) return;
      this.showConfirm(
        "Excluir FIC",
        "Tem certeza que deseja excluir este registro?",
        async () => {
          try {
            this.isLI
              ? await this.ficService.deleteLI(this.clientId, item.id)
              : await this.ficService.deleteLB(this.clientId, item.id);

            this.$notify({
              group: "geral",
              duration: 5000,
              type: "success",
              title: "Sucesso",
              text: "FIC removida com sucesso!",
            });

            this.doLoad();
          } catch (error) {
            this.$notify({
              group: "geral",
              duration: 5000,
              type: "error",
              title: "Erro",
              text: "Erro ao excluir FIC.",
            });
          }
        }
      );
    },
  },
};
</script>
